import { useRef } from "react";
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "framer-motion";
import { IMG } from "@/lib/site";
import { EASE, VIEWPORT, reveal } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";

export default function MomStory() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [40, -80]);

  // Parallax on the wrapper as a composited transform string; the reveal stays
  // on the child. See the same split in Hero.jsx.
  const parallax1 = useMotionTemplate`translate3d(0, ${y1}px, 0)`;
  const parallax2 = useMotionTemplate`translate3d(0, ${y2}px, 0)`;

  return (
    <section
      id="story"
      ref={ref}
      data-testid="story-section"
      className="relative py-12 sm:py-16 md:py-20 bg-[#3B2818] text-[#FAF6F0] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Desktop Left images (lg screens) */}
          <div className="hidden lg:block col-span-12 lg:col-span-5 relative min-h-[440px] order-2 lg:order-1">
            <motion.div
              style={{ transform: parallax1 }}
              className="absolute top-0 left-0 w-[70%] aspect-[3/4]"
            >
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.6, ease: EASE }}
                className="relative w-full h-full overflow-hidden rounded-[20px] grain"
                data-testid="story-image-1"
              >
                <img
                  src={IMG.story.kitchen}
                  alt="Mom in the home kitchen"
                  width="800"
                  height="1066"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
            <motion.div
              style={{ transform: parallax2 }}
              className="absolute bottom-0 right-0 w-[56%] aspect-[4/5]"
            >
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
                className="relative w-full h-full overflow-hidden rounded-[20px] border border-[#FAF6F0]/15 grain"
                data-testid="story-image-2"
              >
                <img
                  src={IMG.story.hands}
                  alt="Hands kneading dough"
                  width="800"
                  height="1000"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>

            {/* signature */}
            <motion.span
              initial={{ opacity: 0, rotate: -10 }}
              whileInView={{ opacity: 1, rotate: -6 }}
              viewport={VIEWPORT}
              transition={{ duration: 0.6, delay: 0.25, ease: EASE }}
              className="absolute -bottom-2 left-4 font-script text-4xl lg:text-5xl text-[#C06A22]"
              data-testid="story-signature"
            >
              — Mom
            </motion.span>
          </div>

          {/* Right story */}
          <div className="col-span-12 lg:col-span-7 order-1 lg:order-2 lg:pl-6">
            <motion.span
              {...reveal()}
              className="text-[11px] uppercase tracking-[0.28em] text-[#C06A22]"
            >
              Mom's story
            </motion.span>

            <motion.h2
              {...reveal(0.06, { y: 24 })}
              className="font-serif text-[28px] sm:text-[36px] md:text-5xl lg:text-6xl leading-[1.05] mt-2 text-[#FAF6F0]"
            >
              A kitchen table,{" "}
              <span className="italic font-light text-[#C06A22]">
                two hands,
              </span>{" "}
              and a lot of butter.
            </motion.h2>

            <motion.div
              {...reveal(0.12)}
              className="mt-4 max-w-xl space-y-3 text-xs sm:text-sm md:text-base leading-relaxed text-[#FAF6F0]/80"
            >
              <p>
                At Signature Spoon, baking for your family the way I bake for mine is the heart of everything I do. 
                Bringing 30+ years of home baking passion and over 10 years of professional expertise,
              </p>
              <p>
                I craft small-batch, real-ingredient treats made to make your moments special.
                Every order is handcrafted from scratch with real ingredients, deep skill, and genuine care.
              </p>
            </motion.div>

            <motion.div {...reveal(0.18)} className="mt-6 sm:mt-8">
              <WhatsAppButton
                variant="caramel"
                size="lg"
                label="Say hi on WhatsApp"
                testId="story-wa-cta"
                msg="Hi! I came from your site — I'd love to know more about your bakes."
              />
            </motion.div>
          </div>

          {/* Mobile Story Images Gallery (under lg screens) */}
          <div className="block lg:hidden col-span-12 order-2 mt-3 relative">
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="relative aspect-[16/10] rounded-[16px] overflow-hidden border border-[#FAF6F0]/15">
                <img
                  src={IMG.story.kitchen}
                  alt="Mom in home kitchen"
                  width="600"
                  height="375"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative aspect-[16/10] rounded-[16px] overflow-hidden border border-[#FAF6F0]/15">
                <img
                  src={IMG.story.hands}
                  alt="Hands kneading dough"
                  width="600"
                  height="375"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="mt-2 text-right pr-2 font-script text-3xl text-[#C06A22]">
              — Mom
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
